import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useRef, useCallback } from "react";
import type { RefObject } from "react";

interface ContinueIndicatorProps {
  show: boolean;
  /** Global index of the page on screen, used to find the content behind. */
  groupIndex: number;
  /**
   * Advance the narrative. People routinely click/tap the indicator itself
   * expecting it to continue, so it doubles as a button.
   */
  onContinue: () => void;
}

/**
 * True when the primary pointer is fine (mouse/trackpad), which is what decides
 * the wording: those people scroll and have a keyboard, so they get
 * "Scroll, or SPACE" — a coarse primary pointer (phone/tablet) gets "Swipe".
 *
 * Deliberately media-query only: a touchscreen laptop reports a fine pointer and
 * should read "Scroll", but any touch-capability probe (`ontouchstart`,
 * `maxTouchPoints`) would misclassify it as a swipe device.
 */
function useHasFinePointer() {
  const [hasFinePointer, setHasFinePointer] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(pointer: fine)");
    const update = () => setHasFinePointer(query.matches);

    update();
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, []);

  return hasFinePointer;
}

/**
 * True when narrative content is rendered behind the indicator's own box.
 *
 * The indicator floats over the page, so where content runs underneath it the
 * white-on-white hint becomes unreadable (worst on a light photo background).
 * A scrim fixes that, but it should only appear when something is actually
 * back there — and only behind the indicator, not as a band across the page.
 *
 * Overlap is measured against `[data-group-items]` inside the current page's
 * scroller — every group stays mounted, and bookmark snapshots re-inject a
 * clone of that same block into the bookmarks overlay, so scoping by group
 * index is what keeps this to the one block that is actually on screen.
 */
function useContentBehind(
  ref: RefObject<HTMLElement | null>,
  enabled: boolean,
  groupIndex: number,
): [boolean, () => void] {
  const [contentBehind, setContentBehind] = useState(false);
  // Held in a ref so the returned remeasure callback is stable across renders.
  const scheduleRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!enabled) {
      setContentBehind(false);
      return;
    }

    const content = document.querySelector(
      `[data-group-content="${groupIndex}"] [data-group-items]`,
    );
    if (!content) {
      setContentBehind(false);
      return;
    }

    let frame = 0;

    const measure = () => {
      frame = 0;
      const node = ref.current;
      if (!node) return;

      const box = node.getBoundingClientRect();
      const rect = content.getBoundingClientRect();
      // An empty block (a group still waiting on the interstitial) has nothing
      // to hide behind the indicator.
      const overlapped =
        rect.width > 0 &&
        rect.height > 0 &&
        rect.left < box.right &&
        rect.right > box.left &&
        rect.top < box.bottom &&
        rect.bottom > box.top;

      setContentBehind(overlapped);
    };

    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(measure);
    };
    scheduleRef.current = schedule;

    measure();

    // Content slides under the indicator as a group's overflow scrolls (capture
    // phase: scroll events don't bubble), and reflows on resize.
    document.addEventListener("scroll", schedule, true);
    window.addEventListener("resize", schedule);

    // Catches late layout — web fonts, images, an item resizing after reveal.
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(schedule);
    observer?.observe(content);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      scheduleRef.current = () => {};
      document.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", schedule);
      observer?.disconnect();
    };
  }, [ref, enabled, groupIndex]);

  const remeasure = useCallback(() => scheduleRef.current(), []);

  return [contentBehind, remeasure];
}

/** Blur radius of the scrim, in px. See the mask below for why it matters. */
const SCRIM_BLUR = 18;

/**
 * Fades the scrim out toward its edges. Applied as a mask, so it feathers the
 * blur and the darkening together — a hard-edged pill reads as a UI chip
 * pasted over the page, which is exactly what we don't want floating mid-story.
 *
 * Two axis gradients intersected, rather than one radial: a radial mask is an
 * ellipse, so it pinches in at its horizontal ends and leaves the first and
 * last words of the hint ("Scroll," and "to continue") outside the scrim with
 * nothing behind them. Per-axis stops cover the whole line evenly instead.
 *
 * Stops are absolute lengths because the constraint is in px: a backdrop-filter
 * only samples within its own element, so its edges clamp, and the mask has to
 * be fully transparent for more than SCRIM_BLUR before the box ends or those
 * clamped pixels show as a seam. Hence 24px of clear margin, then the ramp to
 * opaque — and insets big enough to fit both without eating into the text.
 */
const SCRIM_MASK_X =
  "linear-gradient(to right, transparent 0, transparent 24px, #000 64px, #000 calc(100% - 64px), transparent calc(100% - 24px), transparent 100%)";
const SCRIM_MASK_Y =
  "linear-gradient(to bottom, transparent 0, transparent 24px, #000 48px, #000 calc(100% - 48px), transparent calc(100% - 24px), transparent 100%)";
const SCRIM_MASK = `${SCRIM_MASK_X}, ${SCRIM_MASK_Y}`;

/** Keeps the hint readable where the scrim has already feathered away. */
const HINT_SHADOW = "0 1px 4px rgba(0, 0, 0, 0.55)";

export function ContinueIndicator({
  show,
  groupIndex,
  onContinue,
}: ContinueIndicatorProps) {
  const hasFinePointer = useHasFinePointer();
  const indicatorRef = useRef<HTMLButtonElement>(null);
  const [contentBehind, remeasure] = useContentBehind(
    indicatorRef,
    show,
    groupIndex,
  );

  if (!show) return null;

  return (
    <motion.button
      ref={indicatorRef}
      type="button"
      onClick={() => onContinue()}
      aria-label="Continue"
      initial={{ opacity: 0, y: 20, scale: 0.8 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 10 }}
      transition={{
        type: "spring",
        stiffness: 600,
        damping: 20,
        mass: 1.75,
        duration: 2.8,
      }}
      // The entrance transform moves the box; measure overlap once it settles.
      onAnimationComplete={remeasure}
      whileTap={{ scale: 0.95 }}
      // The wrapper in NarrativeViewer is pointer-events-none so it doesn't
      // block the page; re-enable pointers here so the indicator itself is a
      // real click/tap target with a pointer-cursor hover affordance.
      className="group relative pointer-events-auto cursor-pointer flex flex-col items-center gap-1 @lg:gap-2 px-4 py-1 rounded-full focus:outline-none"
    >
      {/* Scrim, only where content sits behind the indicator. It bleeds past
          the button's box so the mask has room to fade out, and sits first in
          the DOM so the positioned hint and chevron below paint over it.
          A span, not a div: a button takes phrasing content, and absolute
          positioning blockifies this anyway. */}
      <AnimatePresence>
        {contentBehind && (
          <motion.span
            key="scrim"
            data-continue-scrim
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="absolute -inset-x-20 -inset-y-16 pointer-events-none"
            style={{
              backdropFilter: `blur(${SCRIM_BLUR}px)`,
              WebkitBackdropFilter: `blur(${SCRIM_BLUR}px)`,
              backgroundColor: "rgba(0, 0, 0, 0.18)",
              maskImage: SCRIM_MASK,
              WebkitMaskImage: SCRIM_MASK,
              maskComposite: "intersect",
              WebkitMaskComposite: "source-in",
            }}
          />
        )}
      </AnimatePresence>

      {/* Focus ring. It lives on its own layer rather than on the button,
          because a ring is a box-shadow, painted with the button's own
          background — which the scrim, a positioned child, paints over. Placed
          after the scrim in the DOM so it lands on top of it instead. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-full group-focus-visible:ring-2 group-focus-visible:ring-white/90"
      />

      {/* Text hint */}
      <motion.span
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        style={contentBehind ? { textShadow: HINT_SHADOW } : undefined}
        className={`relative group-hover:text-white/90 transition-colors duration-500 text-xs @lg:text-lg font-medium flex items-center gap-1.5 ${
          contentBehind ? "text-white/90" : "text-white/60"
        }`}
      >
        {hasFinePointer ? (
          <>
            Scroll, or{" "}
            <kbd className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/15 border border-white/25 text-white/80 text-[10px] @lg:text-xs font-mono shadow-sm">
              <svg
                className="w-2.5 h-2.5 @lg:w-3 @lg:h-3 opacity-70"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="M6 8h.01M10 8h.01M14 8h.01M18 8h.01M8 12h.01M12 12h.01M16 12h.01M7 16h10" />
              </svg>
              SPACE
            </kbd>
            , to continue
          </>
        ) : (
          "Swipe to continue"
        )}
      </motion.span>

      {/* Animated chevron/arrow pointing down */}
      <motion.span
        className="relative"
        animate={{
          y: [0, 6, 0],
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={
            contentBehind
              ? { filter: `drop-shadow(${HINT_SHADOW})` }
              : undefined
          }
          className={`group-hover:text-white transition-colors duration-500 @lg:w-7 @lg:h-7 ${
            contentBehind ? "text-white" : "text-white/70"
          }`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </motion.span>
    </motion.button>
  );
}
